import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getEventType } from "../../utility/eventType/eventType";
import { todayISODate } from "../../utils/homepageSeason";
import { listDateLabel, squadIsoDates } from "../../utility/squadSpan";
import { getCurrentTournamentStatus } from "../../utils/tournamentStatus";
import { useTournamentFinalizationStatus } from "../../hooks/useTournamentFinalizationStatus";
import { tournamentPath } from "../../utility/tournamentUrls";

const houseName = (tournament) => {
  return (
    tournament.tournamentLocations
      ?.map((location) => location.bowlingCenters?.centerName)
      .find(Boolean) || ""
  );
};

const eventLabel = (tournament) => {
  const events = tournament.tournamentEvents || [];
  const pool = events.some((event) => event.isPrimary)
    ? events.filter((event) => event.isPrimary)
    : events;
  const types = [
    ...new Set(
      pool.map((event) => getEventType(event.bowlerQuantity)).filter(Boolean)
    ),
  ];
  return types.join(" · ");
};

const TournamentCard = ({ tournament }) => {
  const { status, loading, error } = useTournamentFinalizationStatus(
    tournament.listStatus ? null : tournament.id
  );
  const [fetchedStatus, setFetchedStatus] = useState("");

  useEffect(() => {
    if (tournament.listStatus) return;

    const loadStatus = async () => {
      const result = await getCurrentTournamentStatus(
        status,
        false,
        loading,
        error,
        tournament.id
      );
      setFetchedStatus(
        result.status && result.status !== "Loading..." ? result.status : ""
      );
    };

    loadStatus();
  }, [
    status,
    loading,
    error,
    tournament.id,
    tournament.listStatus,
  ]);

  const publicStatus = tournament.listStatus || fetchedStatus;

  const squadDates = squadIsoDates(tournament);
  const pastDate = squadDates.length
    ? squadDates[squadDates.length - 1]
    : tournament.startDate;
  const isPast = Boolean(pastDate && pastDate < todayISODate());
  const organization = tournament.organizations?.name || "";
  const house = houseName(tournament);
  const eventType = eventLabel(tournament);
  const liveStatus =
    publicStatus === "Registration Open" ||
    publicStatus === "Tournament Active";

  return (
    <Link
      to={
        isPast
          ? tournamentPath(tournament, "/standings")
          : tournamentPath(tournament)
      }
      className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-4 py-5 border-b border-brand-charcoal/15 md:items-baseline"
      aria-labelledby={`tournament-${tournament.id}-title`}
    >
      <span
        className={`md:col-span-2 font-display tracking-widest uppercase text-xs ${
          liveStatus ? "text-brand-gold-ink" : "text-brand-muted"
        }`}
      >
        {publicStatus}
      </span>
      <span className="md:col-span-4">
        <span
          id={`tournament-${tournament.id}-title`}
          className="font-display font-bold text-xl text-brand-charcoal"
        >
          {tournament.name}
        </span>
        {eventType ? (
          <span className="block text-sm text-brand-muted mt-0.5">
            {eventType}
          </span>
        ) : null}
      </span>
      <span className="md:col-span-4 text-brand-muted">
        {house ? <span className="block">{house}</span> : null}
        {organization && organization !== house ? (
          <span className="block text-sm mt-0.5">{organization}</span>
        ) : null}
      </span>
      <span className="md:col-span-2 font-score md:text-right">
        {listDateLabel(tournament)}
      </span>
    </Link>
  );
};

export default TournamentCard;
